<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import { SbListItem, SbListLabel, SbListSection } from "@unio/saltbox-ui"

import { dueLabel, timeAgo } from "@/lib/format"
import { ICON } from "@/lib/icons"
import { itemPath, personPath } from "@/routes"
import type { LoanSummary } from "@/types/models"

const props = defineProps<{
  loan: LoanSummary
  /** Show person name in the caption. Omit when the person is already the page context. */
  showPerson?: boolean
  /** Show a Return button on the right side. */
  showReturn?: boolean
  /** Show direction label ("Lent" / "Borrowed") in the caption. */
  showDirection?: boolean
  /** Disable the item link (use when already on the item's page). */
  static?: boolean
}>()

const emit = defineEmits<{
  returned: [loanId: string]
}>()

function directionPrefix(): string {
  return props.loan.direction === "lent" ? "Lent" : "Borrowed"
}

function goToPerson() {
  router.visit(personPath(props.loan.person_id))
}

function markReturned() {
  emit("returned", props.loan.id)
}
</script>

<template>
  <SbListItem :to="static ? undefined : itemPath({ id: loan.item_id })">
    <SbListSection avatar>
      <img
        v-if="loan.item_thumb_url"
        :src="loan.item_thumb_url"
        class="size-8 rounded object-cover"
        :alt="loan.item_name"
      />
      <div v-else class="bg-muted flex size-8 items-center justify-center rounded">
        <UIcon :name="ICON.item" class="text-muted size-4" />
      </div>
    </SbListSection>
    <SbListSection>
      <SbListLabel>{{ loan.item_name }}</SbListLabel>
      <SbListLabel caption>
        <template v-if="showDirection">{{ directionPrefix() }} · </template>
        <span
          v-if="showPerson"
          class="cursor-pointer hover:underline"
          @click.stop.prevent="goToPerson"
          >{{ loan.person_name }}</span
        >
        <span v-if="showPerson"> · </span>
        <template v-if="loan.status === 'returned' && loan.returned_on">
          <span>{{ timeAgo(loan.loaned_on) }}</span>
          <span class="text-muted"> · Returned {{ timeAgo(loan.returned_on) }}</span>
        </template>
        <template v-else>
          <span>{{ timeAgo(loan.loaned_on) }}</span>
          <span v-if="loan.overdue" class="text-error">
            · {{ dueLabel(loan.expected_return_on!) }}</span
          >
          <span v-else-if="loan.expected_return_on">
            · {{ dueLabel(loan.expected_return_on) }}</span
          >
        </template>
      </SbListLabel>
    </SbListSection>
    <SbListSection side>
      <UButton
        v-if="showReturn"
        label="Return"
        size="xs"
        color="neutral"
        variant="subtle"
        @click.prevent="markReturned"
      />
    </SbListSection>
  </SbListItem>
</template>
